// src/pages/Inventory/GoodsReceiptNoteConfirm_Listing.jsx
// ✅ Converted from: GoodsReceiptNoteConfirm_Lisiting.aspx / .aspx.cs + Inv_GRNConfirmListing.cs
// ✅ Uses Node routes: /api/inventory/grn-confirm-listing/*
// ✅ NO rights logic (excluded as instructed)
// ✅ All business logic preserved: HO/Branch toggle, VAT approve/disapprove, filters, grid
// ✅ Pagination added for grid
// ✅ Status badge CSS classes preserved (blinktext equivalents)

import { useEffect, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../../components/HeaderBar";
import LayoutWrapper from "../../../components/LayoutWrapper";
import getCurrentSkin from "../../../utils/getCurrentSkin";
import { ThemeContext } from "../../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search, PlusCircle, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Clock, RefreshCcw} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────── helpers ───────────────────────
const getTodayISO = () => new Date().toISOString().slice(0, 10);

/** Format ISO date → dd/MM/yyyy for display */
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}

/** dd/MM/yyyy → ISO yyyy-MM-dd */
function ddmmyyyyToISO(v) {
  if (!v) return "";
  const parts = v.split("/");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return v;
}

// ─────────────────────── Pagination ───────────────────────
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  let start = Math.max(1, page - 3);
  let end = Math.min(totalPages, start + 6);
  if (end - start < 6) start = Math.max(1, end - 6);
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
      <button onClick={() => onPage(page - 1)} disabled={page === 1}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronLeft size={15} />
      </button>
      {start > 1 && <><button onClick={() => onPage(1)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">1</button><span className="text-xs px-1">…</span></>}
      {pages.map(p => (
        <button key={p} onClick={() => onPage(p)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${p === page ? "bg-blue-600 text-white" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
          {p}
        </button>
      ))}
      {end < totalPages && <><span className="text-xs px-1">…</span><button onClick={() => onPage(totalPages)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">{totalPages}</button></>}
      <button onClick={() => onPage(page + 1)} disabled={page === totalPages}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

// ─────────────────────── Status Badge ───────────────────────
function StatusBadge({ text }) {
  if (!text) return <span className="text-xs text-gray-400">—</span>;
  const map = {
    "Confirmed":  "bg-green-100 text-green-700 border-green-200",
    "Waiting":    "bg-yellow-100 text-yellow-700 border-yellow-200",
    "Approve":    "bg-blue-100 text-blue-700 border-blue-200",
    "DisApprove": "bg-red-100 text-red-700 border-red-200",
    "Changed":    "bg-purple-100 text-purple-700 border-purple-200",
  };
  const cls = map[text] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {text}
    </span>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function GoodsReceiptNoteConfirm_Listing() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const currentSkin = getCurrentSkin();
  const { baseColor, baseShade } = currentSkin;
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session values ──
  const branchId     = Number(localStorage.getItem("branchId") || 0);
  const branchType   = localStorage.getItem("branchType") || "HO";
  const finYearId    = Number(localStorage.getItem("finYearId") || 0);
  const finYearStart = localStorage.getItem("financeyearstartdate") || "";

  // ── style helpers ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/60 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");
  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;
  const cardBase  = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark ? "border-white/10 bg-gray-800/40 backdrop-blur-md" : "border-gray-200 bg-white");
  const thBase = `px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white ${primaryBtnClass}`;
  const tdBase = `px-3 py-2 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`;

  // ── filter state ──
  const [purType,      setPurType]      = useState(branchType === "HO" ? "H" : "B"); // H or B
  const [grnConfNo,    setGrnConfNo]    = useState("");
  const [fromDate,     setFromDate]     = useState("");
  const [toDate,       setToDate]       = useState(getTodayISO());
  const [supplierId,   setSupplierId]   = useState("Select");
  const [storeId,      setStoreId]      = useState("Select");
  const [projectId,    setProjectId]    = useState("Select");
  const [projectCode,  setProjectCode]  = useState("Select");

  // ── dropdown data ──
  const [suppliers,    setSuppliers]    = useState([]);
  const [stores,       setStores]       = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);

  // ── grid state ──
  const [gridData,     setGridData]     = useState([]);
  const [loading,      setLoading]      = useState(false);

  // ── pagination ──
  const [page,         setPage]         = useState(1);
  const PAGE_SIZE = 10;
  const totalPages = Math.ceil(gridData.length / PAGE_SIZE);
  const pagedRows  = gridData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);



  // ── autocomplete ──
  const [acSuggestions, setAcSuggestions] = useState([]);
  const [showAc,        setShowAc]        = useState(false);
  const acRef = useRef(null);

  // ─────────────────────────────────────────────────────
  // Load dropdowns on mount / purType change
  // ─────────────────────────────────────────────────────
  const loadDropdowns = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-listing/meta`, {
        params: { branchId, finYearId, purType },
      });
      if (res.data?.success) {
        setSuppliers(res.data.suppliers || []);
        setStores(res.data.stores || []);
        setProjects(res.data.projects || []);
        setProjectCodes(res.data.projectCodes || []);
        if ((res.data.errors || []).length) {
          toast.error("Failed to load: " + res.data.errors.join("; "));
        }
      }
    } catch (err) {
      console.error("meta error:", err.message);
      toast.error("Failed to load filters: " + (err?.response?.data?.message || err.message));
    }
  }, [branchId, finYearId, purType]);

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  // ─────────────────────────────────────────────────────
  // Load project codes when project name changes
  // ─────────────────────────────────────────────────────
  const loadProjectCodes = useCallback(async (projId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-listing/project-codes`, {
        params: { projectId: projId, branchId, finYearId, branchType },
      });
      if (res.data?.success) setProjectCodes(res.data.projectCodes || []);
    } catch { /* silent */ }
  }, [branchId, finYearId, branchType]);

  const loadProjectNames = useCallback(async (codeId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-listing/project-names`, {
        params: { projectCodeId: codeId, branchId, finYearId, branchType },
      });
      if (res.data?.success) setProjects(res.data.projects || []);
    } catch { /* silent */ }
  }, [branchId, finYearId, branchType]);

  // ─────────────────────────────────────────────────────
  // Autocomplete for GRN Conf No
  // ─────────────────────────────────────────────────────
  const fetchAutoComplete = useCallback(async (prefix) => {
    if (!prefix) { setAcSuggestions([]); setShowAc(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-listing/autocomplete`, {
        params: { prefix, branchId, finYearId, purType,
          supplierId: supplierId !== "Select" ? supplierId : 0,
          storeId: storeId !== "Select" ? storeId : 0 },
      });
      if (res.data?.success) {
        setAcSuggestions(res.data.suggestions || []);
        setShowAc(true);
      }
    } catch { /* silent */ }
  }, [branchId, finYearId, purType, supplierId, storeId]);

  // close autocomplete on outside click
  useEffect(() => {
    const handler = (e) => { if (acRef.current && !acRef.current.contains(e.target)) setShowAc(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ─────────────────────────────────────────────────────
  // Date validation
  // ─────────────────────────────────────────────────────
  function validateDates() {
    const today = getTodayISO();
    if (fromDate && fromDate > today) { toast.warning("From date must be ≤ today"); return false; }
    if (toDate && toDate > today)     { toast.warning("To date must be ≤ today"); return false; }
    if (fromDate && toDate && fromDate > toDate) { toast.warning("From date must be ≤ To date"); return false; }
    if (finYearStart) {
      const fyISO = ddmmyyyyToISO(finYearStart);
      if (fromDate && fromDate < fyISO) { toast.warning(`From date must be ≥ finance year start (${finYearStart})`); return false; }
    }
    return true;
  }

  // ─────────────────────────────────────────────────────
  // Search / bind grid
  // ─────────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    if (!validateDates()) return;
    setLoading(true);
    setPage(1);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-listing/list`, {
        params: {
          branchId, finYearId, purType,
          supplierId:  supplierId  !== "Select" ? supplierId  : "",
          storeId:     storeId     !== "Select" ? storeId     : "",
          grnConfNo:   grnConfNo.trim(),
          fromDate:    fromDate ? fmtDate(fromDate) : "",   // send as dd/MM/yyyy like ASP
          toDate:      toDate   ? fmtDate(toDate)   : "",
          projectId:   projectId  !== "Select" ? projectId   : "",
          projectCode: projectCode !== "Select" ? projectCode : "",
        },
      });
      if (res.data?.success) {
        setGridData(res.data.list || []);
        if ((res.data.list || []).length === 0) toast.info("No records found");
      }
    } catch (err) {
      toast.error("Search failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [branchId, finYearId, purType, supplierId, storeId, grnConfNo, fromDate, toDate, projectId, projectCode]);

  // load grid on mount
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purType]);

  // ─────────────────────────────────────────────────────
  // Row click → UPDATE mode → go to Entry page
  // ASP: gv_grnlist_RowCommand VIEW_GRN
  //      Session["grnconf_purchaseorder"] = GetPurchaseOrders(grnid)
  //      Session["grnconfupdt"] = true
  //      Response.Redirect("GoodsReceiptNoteConfirm_EntryGST.aspx")
  // ─────────────────────────────────────────────────────
const handleRowClick = useCallback(async (row) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-listing/purchase-orders`, {
        params: { grnId: row.fld_grnid, grnInsId: row.fld_grninsid, finYearId },
      });
      if (res.data?.success) {
        console.log("Record set for update", res.data?.success);
        
        sessionStorage.setItem("grnConfirmSelectionPayload", JSON.stringify({
          grnInsId: res.data.grnInsId,
          grn_type: purType,
          grnconf_purchaseorder: res.data.purchaseOrders,
          grnConfHeader: res.data.header,  // ← added: reliable header fields for Entry page
        }));
        sessionStorage.setItem("grnConfirmUpdateMode", "true");
        navigate("/Inventory/GoodsReceiptNoteConfirm_GstEntry");
      }
    } catch (err) {
      toast.error("Failed to load GRN data: " + (err?.response?.data?.message || err.message));
    }
  }, [finYearId, purType, navigate]);

  // ─────────────────────────────────────────────────────
  // Add New → go to Add Listing (selection) page
  // ─────────────────────────────────────────────────────
  const handleAddNew = () => {
    navigate("/Inventory/GoodsReceiptNoteConfirm_Add");
  };



  

  // ─────────────────────────────────────────────────────
  // purType radio change — refill dropdowns + grid
  // ─────────────────────────────────────────────────────
  const handlePurTypeChange = (val) => {
    setPurType(val);
    setSupplierId("Select");
    setStoreId("Select");
    setGrnConfNo("");
    setFromDate("");
    setToDate(getTodayISO());
    setProjectId("Select");
    setProjectCode("Select");
  };

  const isHO = purType === "H";

  // ─────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar title="GRN CONFIRM LISTING" />
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen px-4 py-6 ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>

        {/* ── Filter Card ── */}
        <div className={`${cardBase} mb-6`}>
          {/* Card Header */}
          <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
            <h2 className="text-sm font-bold text-white tracking-wider uppercase">GRN Confirm Listing</h2>
            <button onClick={handleAddNew}
              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition">
              <PlusCircle size={14} /> Add New
            </button>
          </div>

          <div className="p-5 space-y-4">

            {/* Row 0 — Branch Radio */}
            <div className="flex items-center gap-6">
              {["H", "B"].map(val => (
                <label key={val} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="purType" value={val}
                    checked={purType === val}
                    onChange={() => handlePurTypeChange(val)}
                    className="accent-blue-600 w-4 h-4" />
                  <span className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                    {val === "H" ? "H.O" : "Branch"}
                  </span>
                </label>
              ))}
            </div>

            {/* Row 1 — GRN Conf No + Store */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>GRN Confirm No</label>
                <div className="relative mt-1" ref={acRef}>
                  <input
                    value={grnConfNo}
                    onChange={e => { setGrnConfNo(e.target.value); fetchAutoComplete(e.target.value); }}
                    onFocus={() => grnConfNo && setShowAc(acSuggestions.length > 0)}
                    placeholder="Type to search..."
                    className={inputBase}
                  />
                  {showAc && acSuggestions.length > 0 && (
                    <div className={`absolute z-30 left-0 right-0 mt-1 rounded-xl border shadow-xl max-h-48 overflow-y-auto
                      ${isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200"}`}>
                      {acSuggestions.map((s, i) => (
                        <div key={i}
                          onClick={() => { setGrnConfNo(s); setShowAc(false); }}
                          className={`px-3 py-2 text-sm cursor-pointer
                            ${isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}>
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className={labelBase}>Store <span className="text-red-500">*</span></label>
                <select value={storeId} onChange={e => { setStoreId(e.target.value); fetchAutoComplete(grnConfNo); }}
                  className={inputBase + " mt-1"}>
                  <option value="Select">Select</option>
                  {stores.map(s => <option key={s.fld_storeid} value={s.fld_storeid}>{s.fld_storename}</option>)}
                </select>
              </div>
            </div>

            {/* Row 2 — From Date + To Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>From Date</label>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  max={getTodayISO()} min={ddmmyyyyToISO(finYearStart)}
                  className={inputBase + " mt-1"} />
              </div>
              <div>
                <label className={labelBase}>To Date</label>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                  max={getTodayISO()} min={ddmmyyyyToISO(finYearStart)}
                  className={inputBase + " mt-1"} />
              </div>
            </div>

            {/* Row 3 — Project Name + Supplier */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Project Name</label>
                <select value={projectId}
                  onChange={e => {
                    setProjectId(e.target.value);
                    if (e.target.value !== "Select") loadProjectCodes(e.target.value);
                    else loadDropdowns();
                  }}
                  className={inputBase + " mt-1"}>
                  <option value="Select">Select</option>
                  {projects.map(p => <option key={p.fld_projectid} value={p.fld_projectid}>{p.fld_projectname}</option>)}
                </select>
              </div>
              <div>
                <label className={labelBase}>Supplier</label>
                <select value={supplierId}
                  onChange={e => { setSupplierId(e.target.value); fetchAutoComplete(grnConfNo); }}
                  className={inputBase + " mt-1"}>
                  <option value="Select">Select</option>
                  {suppliers.map(s => <option key={s.fld_supplierid} value={s.fld_supplierid}>{s.fld_suppliername}</option>)}
                </select>
              </div>
            </div>

            {/* Row 4 — Project Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Project Code</label>
                <select value={projectCode}
                  onChange={e => {
                    setProjectCode(e.target.value);
                    if (e.target.value !== "Select") loadProjectNames(e.target.value);
                    else loadDropdowns();
                  }}
                  className={inputBase + " mt-1"}>
                  <option value="Select">Select</option>
                  {projectCodes.map(p => <option key={p.fld_projectid} value={p.fld_projectid}>{p.fld_projectcode}</option>)}
                </select>
              </div>
            </div>

            {/* Search Button */}
            <div className="flex justify-center pt-1">
              <button onClick={handleSearch} disabled={loading}
                className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}>
                {loading ? <RefreshCcw size={15} className="animate-spin" /> : <Search size={15} />}
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>


        {/* ── Grid Card ── */}
        <div className={cardBase}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={thBase}>Sl No</th>
                  <th className={thBase}>GRN Confirm No</th>
                  <th className={thBase}>Date</th>
                  <th className={thBase}>Supplier</th>
                  {isHO && <th className={thBase}>Branch</th>}
                  <th className={thBase}>Bill Pass Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isHO ? 6 : 5} className={`${tdBase} text-center py-10`}>
                      <RefreshCcw size={20} className="animate-spin inline mr-2 text-blue-500" />
                      Loading...
                    </td>
                  </tr>
                ) : pagedRows.length === 0 ? (
                  <tr>
                    <td colSpan={isHO ? 6 : 5} className={`${tdBase} text-center py-10 text-gray-400`}>
                      No Records Found
                    </td>
                  </tr>
                ) : pagedRows.map((row, idx) => {
                            const globalIdx = (page - 1) * PAGE_SIZE + idx;

                  return (
                    <tr key={globalIdx}
                      className={`border-t transition ${isDark ? "border-white/5 hover:bg-white/5" : "border-gray-100 hover:bg-blue-50/40"}
                        ${globalIdx % 2 === 0 ? (isDark ? "bg-gray-800/20" : "bg-gray-50/60") : ""}`}>
                      <td className={`${tdBase} text-center`}>{globalIdx + 1}</td>
                      {/* GRN Conf No — clickable link */}
                      <td className={tdBase}>
                        <button
                          onClick={() => handleRowClick(row)}
                          className="text-blue-500 hover:text-blue-700 font-semibold underline underline-offset-2 text-xs transition">
                          {row.FLD_grnconfNo || row.fld_grnconfno || row.fld_branchwiseno}
                        </button>
                      </td>
                      <td className={tdBase}>{row.fld_date}</td>
                                        <td className={tdBase}>{row.fld_suppliername}</td>
                      {isHO && <td className={tdBase}>{row.fld_branchName}</td>}
                      <td className={`${tdBase} text-center`}>
                        <StatusBadge text={row.Status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {gridData.length > 0 && (
            <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2
              ${isDark ? "border-white/10 text-gray-400" : "border-gray-100 text-gray-500"}`}>
              <span>Showing {Math.min((page-1)*PAGE_SIZE+1, gridData.length)}–{Math.min(page*PAGE_SIZE, gridData.length)} of {gridData.length}</span>
              <Pagination page={page} totalPages={totalPages} onPage={p => setPage(p)} />
            </div>
          )}
        </div>

      </div>
    </LayoutWrapper>
  );
}